import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useAccountPreferences, useUpdateAccountPreferences } from "@/queries/account";
import { Vibrate } from "lucide-react";
import { toast } from "sonner";
import { getNativePlatform } from "@/lib/runtime";
import { NativeSettingsSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";
import { useHaptics } from "@/haptics/HapticsProvider";

const INTENSITY_OPTIONS = [
  { value: "weak", label: "Svag" },
  { value: "normal", label: "Normal" },
  { value: "strong", label: "Stark" },
];

export default function HapticsSettingCard() {
  const { data, isLoading } = useAccountPreferences();
  const updatePreferences = useUpdateAccountPreferences();
  const {
    previewIntensity,
    setEnabled: setHapticsEnabled,
    setIntensity: setHapticsIntensity,
  } = useHaptics();
  const [allowHaptics, setAllowHaptics] = useState(
    () => data?.preferences?.allow_haptics !== false
  );
  const [hapticIntensity, setHapticIntensity] = useState(
    () => data?.preferences?.haptic_intensity || "normal"
  );

  useEffect(() => {
    setAllowHaptics(data?.preferences?.allow_haptics !== false);
    setHapticIntensity(data?.preferences?.haptic_intensity || "normal");
  }, [data]);

  if (isLoading && getNativePlatform() === "android") {
    return <NativeSettingsSkeleton sections={1} />;
  }

  const toggleAllowHaptics = async (checked) => {
    if (checked === allowHaptics || updatePreferences.isPending) return;

    setAllowHaptics(checked);
    setHapticsEnabled(checked);
    try {
      await toast.promise(
        updatePreferences.mutateAsync({
          preferences: {
            auto_scroll_next_heat: data?.preferences?.auto_scroll_next_heat !== false,
            allow_haptics: checked,
            haptic_intensity: hapticIntensity,
            protocol_default_view: data?.preferences?.protocol_default_view || "cards",
            protocol_default_view_confirmed:
              data?.preferences?.protocol_default_view_confirmed || false,
          },
        }),
        {
          loading: "Sparar inställning...",
          success: checked ? "Vibrationer aktiverade" : "Vibrationer avstängda",
          error: (error) => error?.message || "Kunde inte spara inställningen",
        }
      );
    } catch {
      setAllowHaptics(!checked);
      setHapticsEnabled(!checked);
    }
  };

  const updateIntensity = async (nextIntensity) => {
    if (!allowHaptics || nextIntensity === hapticIntensity || updatePreferences.isPending) return;

    const previousIntensity = hapticIntensity;
    setHapticIntensity(nextIntensity);
    setHapticsIntensity(nextIntensity);
    previewIntensity(nextIntensity);
    try {
      await updatePreferences.mutateAsync({
        preferences: {
          auto_scroll_next_heat: data?.preferences?.auto_scroll_next_heat !== false,
          allow_haptics: allowHaptics,
          haptic_intensity: nextIntensity,
          protocol_default_view: data?.preferences?.protocol_default_view || "cards",
          protocol_default_view_confirmed:
            data?.preferences?.protocol_default_view_confirmed || false,
        },
      });
    } catch (error) {
      setHapticIntensity(previousIntensity);
      setHapticsIntensity(previousIntensity);
      toast.error(error?.message || "Kunde inte spara vibrationsstyrkan");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Vibrate className="h-5 w-5 shrink-0 opacity-70" />
          <span>Vibrationer</span>
        </CardTitle>
        <CardDescription>
          Styr om appen får ge haptisk feedback vid utvalda interaktioner på enheter som stöder det.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-xl border border-border bg-muted/20 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="font-medium">Tillåt vibrationer</div>
              <p className="text-sm text-muted-foreground">
                Används för haptisk feedback vid vissa knapptryck och bekräftelser. På enheter eller webbläsare utan stöd används funktionen inte.
              </p>
            </div>
            <Switch
              checked={allowHaptics}
              disabled={isLoading || updatePreferences.isPending}
              onCheckedChange={toggleAllowHaptics}
              aria-label="Tillåt vibrationer"
            />
          </div>
          <div className="native-v3-haptic-intensity" data-disabled={!allowHaptics ? "true" : "false"}>
            <span>Vibrationsstyrka</span>
            <div role="group" aria-label="Vibrationsstyrka">
              {INTENSITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  data-active={hapticIntensity === option.value ? "true" : "false"}
                  disabled={!allowHaptics || isLoading || updatePreferences.isPending}
                  onClick={() => updateIntensity(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
